/**
 * @file useWaiterStats.ts
 * @description Hook managing waiter personal performance statistics and timeframe switching.
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchWaiterPersonalStats } from '../services/waiterStats.service';
import type { WaiterPersonalStats, WaiterStatsTimeframe } from '../../../types/waiter.types';

export const useWaiterStats = (venueId: string | null) => {
  const [timeframe, setTimeframe] = useState<WaiterStatsTimeframe>('day');
  const [stats, setStats] = useState<WaiterPersonalStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    if (!venueId) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchWaiterPersonalStats(venueId, timeframe);
      setStats(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load personal metrics');
    } finally {
      setIsLoading(false);
    }
  }, [venueId, timeframe]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    timeframe,
    setTimeframe,
    stats,
    isLoading,
    error,
    refreshStats: loadStats,
  };
};
